import { match, P } from 'ts-pattern';

import { showFlash } from '@/alerts';
import { warn } from '@/logger';
import { useAppInfoStore } from '@/stores/appInfo';
import { useLicensingStore } from '@/stores/licensing';
import { routeTo } from '@/utils/cluster-utils';
import { pluralize } from '@/utils/format-utils';

type Warning = readonly [
  message: string,
  details: string,
  buttons?: ReadonlyArray<{
    clickFn?: VoidFunction;
    classes?: string;
    icon?: string;
    text: string;
  }>,
  toLog?: boolean,
];

const CONTACT_SALES_URL = 'https://www.veeam.com/contact-sales.html';

const getRemoveOfflineModeMessage = () => {
  const appInfoStore = useAppInfoStore();
  return `ensure that Kasten has access to the Internet. See https://docs.kasten.io/${appInfoStore.systemVersion}/install/offline#running-k10-within-a-local-network for details on airgap configuration`;
};
const REMOVE_PHONEHOME_BLOCK = `ensure that Kasten has access to the Internet`;

const nodeLimitExceededWarning = (current: number, limit: number) => {
  return `The system is using ${current} worker nodes which exceeds the limit of ${limit} nodes for this license.`;
};

const remainingGracePeriod = (remainingMinutes: number) => {
  if (remainingMinutes <= 0) return '0 days';

  // More than 2 days
  if (remainingMinutes > 48 * 60) {
    return `${pluralize(Math.floor(remainingMinutes / (24 * 60)), 'day')}`;
  }

  if (remainingMinutes > 120) return `${pluralize(Math.floor(remainingMinutes / 60), 'hour')}`;

  return `${pluralize(Math.floor(remainingMinutes), 'minute')}`;
};

export const getSupportMessage = (): string => {
  const licensingStore = useLicensingStore();

  if (licensingStore.isGracePeriod && licensingStore.licenseStatus?.gracePeriod?.available) {
    return `${remainingGracePeriod(
      licensingStore.licenseStatus?.gracePeriod?.remainingMinutes ?? 0,
    )} remain in the grace period.`;
  }

  if (licensingStore.isGracePeriodExpired && licensingStore.licenseStatus?.gracePeriod?.available) {
    return 'The grace period has expired. Some functionality has been disabled.';
  }

  return '';
};

export const getInvalidMessage = () => {
  return [
    'Invalid License',
    `The license installed on this system has an invalid signature and cannot be trusted. ${getSupportMessage()}`.trim(),
  ] as const;
};

export const getExpiredMessage = () => {
  return [
    'Expired License',
    `The license installed on this system has expired. ${getSupportMessage()}`.trim(),
  ] as const;
};

export const getLimitExceededMessage = () => {
  const licensingStore = useLicensingStore();
  const support = getSupportMessage();
  return [
    'License Limit Exceeded',
    `${nodeLimitExceededWarning(
      licensingStore.licenseStatus?.nodeLimit?.current ?? 0,
      licensingStore.licenseStatus?.nodeLimit?.limit ?? 0,
    )} Some functionality will be disabled. You can remove this restriction by upgrading your license.${support ? ` ${support}` : ''}`,
  ] as const;
};

export const getAwsMeteringFailedMessage = () => {
  return [
    'Unable to connect to AWS Marketplace billing',
    `Please verify that Kasten Service Account has arn:aws:iam::aws:policy/AWSMarketplaceMeteringFullAccess and runs inside EKS`,
  ] as const;
};

export const getAwsTotemFailedMessage = () => {
  const licensingStore = useLicensingStore();
  const support = getSupportMessage();
  return [
    'Unable to obtain AWS license entitlements',
    `Please verify that Kasten Service Account can obtain license entitlements for ${
      licensingStore.licenseStatus?.nodeLimit?.current ?? 0
    } nodes.${support ? ` ${support}` : ''}`,
  ] as const;
};

export const getRedHatMeteringFailedMessage = () => {
  return [
    'Unable to validate Red Hat Marketplace license',
    `Please verify that Kasten is installed after registering the cluster with Red Hat Marketplace or after installing the Red Hat Marketplace Operator.`,
  ] as const;
};

export const getAzureMeteringFailedMessage = () => {
  return [
    'Unable to validate Azure Marketplace license',
    `Please verify that Kasten is installed after registering the cluster with Azure Marketplace.`,
  ] as const;
};

export const getInvalidOfflineRedHatMessage = () => {
  const licensingStore = useLicensingStore();
  const support = getSupportMessage();
  return [
    'License invalid in disconnected environments',
    `Please ${
      licensingStore.licenseStatus?.phoneHomeStatus === 'offlineMode'
        ? getRemoveOfflineModeMessage()
        : REMOVE_PHONEHOME_BLOCK
    } or upgrade to a term license.${support ? ` ${support}` : ''}`,
  ] as const;
};

export const getInvalidOfflineMessage = () => {
  const licensingStore = useLicensingStore();
  const support = getSupportMessage();
  return [
    'License invalid in disconnected environments',
    `Please ${
      licensingStore.licenseStatus?.phoneHomeStatus === 'offlineMode'
        ? getRemoveOfflineModeMessage()
        : REMOVE_PHONEHOME_BLOCK
    }.${support ? ` ${support}` : ''}`,
  ] as const;
};

export const getNoLicenseMessage = () => {
  const support = getSupportMessage();
  return [
    'No licenses found',
    `No licenses were found on this system.${support ? ` ${support}` : ''}`,
    [
      {
        text: 'See License Settings for details',
        clickFn: () => routeTo({ name: 'LicenseSettings' }),
      },
    ],
    false,
  ] as const;
};

export const getWarning = (): Warning | null => {
  const licensingStore = useLicensingStore();
  return match(licensingStore.licenseStatus?.status)
    .with('invalidSignature', getInvalidMessage)
    .with('licenseExpired', getExpiredMessage)
    .with('nodeLimitExceeded', getLimitExceededMessage)
    .with('awsMarketMeteringFailed', getAwsMeteringFailedMessage)
    .with('awsTotemFailed', getAwsTotemFailedMessage)
    .with('redhatCloudMeteringFailed', getRedHatMeteringFailedMessage)
    .with('azureCloudMeteringFailed', getAzureMeteringFailedMessage)
    .with('nolicensesfound', getNoLicenseMessage)
    .with('invalidOffline', () =>
      match([licensingStore.licenseStatus?.licenseType, licensingStore.isPhoneHomeStatusInactive])
        .with(['redhatCloudMetering', true], getInvalidOfflineRedHatMessage)
        .with([P._, true], getInvalidOfflineMessage)
        .otherwise(() => null),
    )
    .otherwise(() => null);
};

const contactSalesButton = {
  text: 'Contact Veeam Sales',
  clickFn: () => window.open(CONTACT_SALES_URL, '_blank', 'noopener,noreferrer'),
};

const showWarning = ([message, details, buttons, toLog = true]: Warning) => {
  showFlash({
    message,
    details,
    messageType: 'danger',
    icon: 'icon-alert',
    dismiss: 'user',
    closable: true,
    buttons: [...(buttons ?? []), contactSalesButton],
  });
  if (toLog) {
    warn(`Showing license warning: ${message}`);
  }
};

export const considerShowingNotifications = () => {
  const warning = getWarning();

  if (warning !== null) showWarning(warning);
};

// Used to determine if we should show a warning message about disabled functionality.
// Current threshold is 1 minute
export const tooLongSinceValidLicense = (time: number) => Date.now() - time > 6e4;
